import { useEffect } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { PauseCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Client, LIFECYCLE_STAGES } from '@/types/crm';
import { ClientPipelineColumn } from './ClientPipelineColumn';

interface PausedRailProps {
  clients: Client[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onClientClick?: (client: Client) => void;
  onQuickMove?: (client: Client) => void;
  savingClientId?: string | null;
  isBulkMode?: boolean;
  selectedClientIds?: Set<string>;
  onToggleSelect?: (clientId: string) => void;
}

/**
 * Collapsible "Paused" lane pinned to the left of the board.
 *
 * Collapsed it is a narrow vertical tab; expanded it is an ordinary pipeline
 * column. Both states register the same `paused` droppable id, so a card can be
 * dropped straight onto the tab without opening it — and dragging over the tab
 * auto-expands it so you can see where the card is landing.
 */
export function PausedRail({
  clients,
  isOpen,
  onOpenChange,
  onClientClick,
  onQuickMove,
  savingClientId,
  isBulkMode,
  selectedClientIds,
  onToggleSelect,
}: PausedRailProps) {
  // Only mounted while collapsed; the expanded column registers `paused` itself.
  const { setNodeRef, isOver } = useDroppable({ id: 'paused', disabled: isOpen });

  // Auto-expand when a card is dragged over the collapsed tab.
  useEffect(() => {
    if (isOver && !isOpen) onOpenChange(true);
  }, [isOver, isOpen, onOpenChange]);

  if (isOpen) {
    return (
      <div className="relative flex-shrink-0 w-[85vw] min-w-[200px] snap-center sm:w-56">
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          aria-label="Collapse paused"
          className="absolute -right-1 top-3 z-20 flex h-7 w-7 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <ClientPipelineColumn
          stage="paused"
          clients={clients}
          onClientClick={onClientClick}
          onQuickMove={onQuickMove}
          savingClientId={savingClientId}
          isBulkMode={isBulkMode}
          selectedClientIds={selectedClientIds}
          onToggleSelect={onToggleSelect}
        />
      </div>
    );
  }

  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={() => onOpenChange(true)}
      aria-expanded={false}
      aria-label={`Expand paused (${clients.length})`}
      className={`
        flex-shrink-0 snap-center self-stretch min-h-[280px] w-11
        flex flex-col items-center gap-3 rounded-lg border bg-card py-3
        text-muted-foreground transition-colors hover:text-foreground hover:bg-accent/30
        ${isOver ? 'ring-2 ring-primary ring-offset-2 bg-accent/30' : 'border-border'}
      `}
    >
      <ChevronRight className="h-4 w-4" />
      <PauseCircle className="h-4 w-4" />
      <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-foreground">
        {clients.length}
      </span>
      <span className="text-xs font-medium [writing-mode:vertical-rl]">
        {LIFECYCLE_STAGES.paused.label}
      </span>
    </button>
  );
}
